import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { useApplicationState } from '@atlas/core';
import { NotificationTopic, TransportManager } from '@atlas/transport';

import {
  bonusStatuses,
  bonusTypes,
  CASINO_PRODUCT_TYPE,
  SPORTSBOOK_PRODUCT_TYPE,
} from 'src/common/constants';
import {
  checkDepositBonusTypeFits,
  findLatestActivatedBonus,
  findTransportMatchedBonus,
  getBonusMinDeposit,
  normalizeBonusWithFinanceData,
} from 'src/common/utils/depositBonusUtils';
import { sortBonusesByAvailabilityAndAmount } from 'src/common/utils/sortBonusesByAvailabilityAndAmount';
import useBonuses from 'src/hooks/useBonuses';

const EMPTY_BONUSES = [];
const EMPTY_CONTEXT_SETTER = () => {};

const { NO_DEPOSIT, RELOAD_NO_DEPOSIT } = bonusTypes;
const { ACTIVATED, IN_PROGRESS } = bonusStatuses;

const isCasinoDeposit = bonus =>
  checkDepositBonusTypeFits(bonus?.bonusType) && bonus?.productType === CASINO_PRODUCT_TYPE;

const orderBonusesByAvailability = bonusesList => {
  const bonusesBuckets = bonusesList.reduce(
    (accumulator, currentBonus) => {
      const bucketKey = currentBonus.disabled ? 'disabled' : 'enabled';

      accumulator[bucketKey].push(currentBonus);

      return accumulator;
    },
    { disabled: [], enabled: [] },
  );

  return [...bonusesBuckets.enabled, ...bonusesBuckets.disabled];
};

export const QuickDepositBonusContext = createContext({
  activatedBonus: null,
  amountToGetBonus: 0,
  availableBonuses: EMPTY_BONUSES,
  bonusesList: EMPTY_BONUSES,
  depositBonuses: EMPTY_BONUSES,
  inputValue: '',
  isBonusesDisabled: false,
  paymentMethod: null,
  paymentMethodId: null,
  selectedBonus: null,
  setDepositAmount: EMPTY_CONTEXT_SETTER,
  setInputValue: EMPTY_CONTEXT_SETTER,
  setPaymentMethod: EMPTY_CONTEXT_SETTER,
  setPaymentMethodId: EMPTY_CONTEXT_SETTER,
  setSelectedBonus: EMPTY_CONTEXT_SETTER,
  shouldRenderInSlider: false,
  showBonus: true,
});

export const useQuickDepositBonusContext = () => useContext(QuickDepositBonusContext);

const useQuickDepositBonusValue = ({
  depositAmount = '',
  paymentMethod = null,
  paymentMethodId = null,
}) => {
  const resolvedPaymentMethod = useMemo(
    () => paymentMethod ?? (paymentMethodId == null ? null : { id: paymentMethodId }),
    [paymentMethod, paymentMethodId],
  );
  const applicationState = useApplicationState();
  const { account = {} } = applicationState;
  const { country, currency: accountCurrency } = account;
  const {
    activeBonuses = EMPTY_BONUSES,
    bonuses = EMPTY_BONUSES,
    isLoading,
    wallet = {},
  } = useBonuses();
  const currency = accountCurrency ?? wallet.currency;

  const [selectedBonus, setSelectedBonus] = useState(null);
  const [isBonusesDisabled, setIsBonusesDisabled] = useState(false);
  const [depositCompleteBonus, setDepositCompleteBonus] = useState(null);
  const [depositCompletePaymentMethodId, setDepositCompletePaymentMethodId] = useState(null);
  const [forcedSliderMode, setForcedSliderMode] = useState(false);
  const [
    hasPaymentMethodChangedSinceDepositComplete,
    setHasPaymentMethodChangedSinceDepositComplete,
  ] = useState(false);
  const intendedBonusRef = useRef(null);

  const getMinDeposit = useCallback(
    bonus => getBonusMinDeposit(bonus, currency),
    [currency],
  );

  const resetCompletedDepositState = useCallback(() => {
    setDepositCompleteBonus(null);
    setDepositCompletePaymentMethodId(null);
    setHasPaymentMethodChangedSinceDepositComplete(false);
  }, []);

  const applyActivatedBonusState = useCallback(activatedBonusData => {
    const nextBonus = activatedBonusData?.disabled ? null : activatedBonusData;

    setSelectedBonus(nextBonus);
    setIsBonusesDisabled(true);
    intendedBonusRef.current = nextBonus;
  }, []);

  const keepBonusAsIntention = useCallback(() => {
    setSelectedBonus(currentSelectedBonus => {
      if (!currentSelectedBonus) {
        return currentSelectedBonus;
      }

      intendedBonusRef.current = currentSelectedBonus;

      return null;
    });
  }, []);

  const restoreIntendedBonus = useCallback(() => {
    if (!intendedBonusRef.current) {
      return;
    }

    setSelectedBonus(intendedBonusRef.current);
    intendedBonusRef.current = null;
  }, []);

  const rememberCompletedDepositBonus = useCallback(
    bonus => {
      intendedBonusRef.current = bonus;
      setSelectedBonus(bonus);
      setDepositCompleteBonus(bonus);
      setDepositCompletePaymentMethodId(resolvedPaymentMethod?.id ?? null);
      setHasPaymentMethodChangedSinceDepositComplete(false);
      setIsBonusesDisabled(true);
      setForcedSliderMode(true);
    },
    [resolvedPaymentMethod?.id],
  );

  const isBonusAllowed = useCallback(
    bonus => {
      const normalizedBonus = bonus?.bonus ?? bonus;
      const { basicLimit = {} } = normalizedBonus;
      const notAllowedCountries = (basicLimit.notAllowedCountries ?? []).map(item =>
        item?.toLowerCase(),
      );
      const notAllowedPaymentMethods = basicLimit.notAllowedPaymentMethods ?? [];

      return (
        !notAllowedPaymentMethods.includes(resolvedPaymentMethod?.id?.toString()) &&
        !notAllowedCountries.includes(country?.toLowerCase())
      );
    },
    [country, resolvedPaymentMethod?.id],
  );

  const depositBonuses = useMemo(() => {
    if (!resolvedPaymentMethod?.id) {
      return EMPTY_BONUSES;
    }

    const activeDepositBonuses = activeBonuses
      .filter(bonus => {
        const { bonus: normalizedBonus = {}, status } = bonus;

        return status === ACTIVATED && isCasinoDeposit(normalizedBonus);
      })
      .map(bonus => ({
        ...normalizeBonusWithFinanceData(bonus, currency),
        disabled: !isBonusAllowed(bonus),
      }))
      .sort(sortBonusesByAvailabilityAndAmount);

    const availableDepositBonuses = bonuses
      .filter(isCasinoDeposit)
      .map(bonus => ({
        ...normalizeBonusWithFinanceData(bonus, currency),
        disabled: !isBonusAllowed(bonus),
      }))
      .sort(sortBonusesByAvailabilityAndAmount);

    return [...activeDepositBonuses, ...availableDepositBonuses];
  }, [activeBonuses, bonuses, currency, isBonusAllowed, resolvedPaymentMethod?.id]);

  const internalActivatedBonus = useMemo(() => {
    const latestActivatedBonus = findLatestActivatedBonus({
      activeBonuses,
      isMatchingBonus: isCasinoDeposit,
    });

    if (!latestActivatedBonus) {
      return null;
    }

    const normalizedBonus = normalizeBonusWithFinanceData(latestActivatedBonus, currency);

    return {
      ...normalizedBonus,
      disabled: !isBonusAllowed(normalizedBonus),
    };
  }, [activeBonuses, currency, isBonusAllowed]);

  const hasActivatedSportsbookBonus = useMemo(() => {
    return activeBonuses.some(({ bonus, status }) => {
      return (
        status === ACTIVATED &&
        checkDepositBonusTypeFits(bonus?.bonusType) &&
        bonus?.productType === SPORTSBOOK_PRODUCT_TYPE
      );
    });
  }, [activeBonuses]);

  const typesToHide = useMemo(() => {
    return activeBonuses
      .filter(
        active =>
          active.status === IN_PROGRESS &&
          (active.bonus?.bonusType === NO_DEPOSIT ||
            active.bonus?.bonusType === RELOAD_NO_DEPOSIT ||
            active.bonus?.productType === CASINO_PRODUCT_TYPE ||
            active.bonus?.productType === SPORTSBOOK_PRODUCT_TYPE),
      )
      .map(active => active.bonus?.productType);
  }, [activeBonuses]);

  const depositBonusesToShow = useMemo(() => {
    const filteredBonuses = typesToHide.length
      ? depositBonuses.filter(bonus => !typesToHide.includes(bonus.productType))
      : depositBonuses;

    return orderBonusesByAvailability(filteredBonuses);
  }, [depositBonuses, typesToHide]);

  const findBonusByTransportPayload = useCallback(
    ({ bonusId, bonusInstanceId } = {}) => {
      return findTransportMatchedBonus({
        activeBonuses,
        bonuses,
        bonusId,
        bonusInstanceId,
        currency,
        isMatchingBonus: isCasinoDeposit,
      });
    },
    [activeBonuses, bonuses, currency],
  );

  useEffect(() => {
    if (resolvedPaymentMethod?.id) {
      return;
    }

    setSelectedBonus(null);
    setIsBonusesDisabled(false);
    intendedBonusRef.current = null;
    setForcedSliderMode(false);
    resetCompletedDepositState();
  }, [resetCompletedDepositState, resolvedPaymentMethod?.id]);

  useEffect(() => {
    const handleDepositComplete = transportMatchedBonus => {
      const completedBonus =
        transportMatchedBonus ||
        internalActivatedBonus ||
        selectedBonus ||
        intendedBonusRef.current;

      if (!completedBonus) {
        return;
      }

      rememberCompletedDepositBonus(completedBonus);
    };

    const unsubscribe = TransportManager.getInstance().subscribe(
      {
        notificationTopic: NotificationTopic.BONUS_INSTANCE_STATUS_CHANGED,
      },
      data => {
        const completedBonus = findBonusByTransportPayload(data);

        handleDepositComplete(completedBonus);
      },
    );

    return unsubscribe;
  }, [
    findBonusByTransportPayload,
    internalActivatedBonus,
    rememberCompletedDepositBonus,
    selectedBonus,
  ]);

  useEffect(() => {
    if (
      depositCompletePaymentMethodId == null ||
      hasPaymentMethodChangedSinceDepositComplete ||
      resolvedPaymentMethod?.id == null
    ) {
      return;
    }

    if (depositCompletePaymentMethodId !== resolvedPaymentMethod.id) {
      setHasPaymentMethodChangedSinceDepositComplete(true);
    }
  }, [
    depositCompletePaymentMethodId,
    hasPaymentMethodChangedSinceDepositComplete,
    resolvedPaymentMethod?.id,
  ]);

  useEffect(() => {
    if (internalActivatedBonus) {
      resetCompletedDepositState();
    }
  }, [internalActivatedBonus, resetCompletedDepositState]);

  useEffect(() => {
    if (internalActivatedBonus || depositCompleteBonus) {
      return;
    }

    setForcedSliderMode(false);
  }, [depositCompleteBonus, internalActivatedBonus]);

  useEffect(() => {
    if (internalActivatedBonus) {
      applyActivatedBonusState(internalActivatedBonus);
      return;
    }

    if (depositCompleteBonus) {
      setSelectedBonus(depositCompleteBonus.disabled ? null : depositCompleteBonus);
      setIsBonusesDisabled(true);
      intendedBonusRef.current = depositCompleteBonus.disabled ? null : depositCompleteBonus;
      return;
    }

    if (hasActivatedSportsbookBonus) {
      intendedBonusRef.current = null;
      setSelectedBonus(null);
      setIsBonusesDisabled(true);
      return;
    }

    const firstEnabledBonus = depositBonusesToShow.find(bonus => !bonus.disabled) || null;
    const findEnabledBonusById = bonus =>
      depositBonusesToShow.find(item => item.id === bonus?.id && !item.disabled) || null;

    if (intendedBonusRef.current && !findEnabledBonusById(intendedBonusRef.current)) {
      intendedBonusRef.current = null;
    }

    setSelectedBonus(prevSelectedBonus => {
      if (prevSelectedBonus) {
        return findEnabledBonusById(prevSelectedBonus) || firstEnabledBonus;
      }

      if (intendedBonusRef.current || !firstEnabledBonus) {
        return null;
      }

      return firstEnabledBonus;
    });
  }, [
    applyActivatedBonusState,
    depositBonusesToShow,
    depositCompleteBonus,
    hasActivatedSportsbookBonus,
    internalActivatedBonus,
  ]);

  useEffect(() => {
    if (depositCompleteBonus) {
      setIsBonusesDisabled(true);
    }
  }, [depositCompleteBonus]);

  useEffect(() => {
    if (internalActivatedBonus || depositCompleteBonus) {
      return;
    }

    const bonusToCheck = intendedBonusRef.current || selectedBonus;

    if (!bonusToCheck) {
      return;
    }

    if (depositAmount === '') {
      setIsBonusesDisabled(false);

      return;
    }

    const currentAmount = Number(depositAmount);
    const requiredAmount = getMinDeposit(bonusToCheck);

    if (currentAmount >= requiredAmount) {
      restoreIntendedBonus();
      setIsBonusesDisabled(false);
      return;
    }

    if (currentAmount > 0) {
      keepBonusAsIntention();
      setIsBonusesDisabled(true);
    }
  }, [
    depositAmount,
    depositCompleteBonus,
    getMinDeposit,
    internalActivatedBonus,
    keepBonusAsIntention,
    restoreIntendedBonus,
    selectedBonus,
  ]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      if (!hasPaymentMethodChangedSinceDepositComplete) {
        resetCompletedDepositState();
      }

      if (!bonus || bonus.disabled) {
        intendedBonusRef.current = null;
        setSelectedBonus(null);

        return;
      }

      if (isBonusesDisabled) {
        intendedBonusRef.current = bonus;
        setSelectedBonus(null);
      } else {
        intendedBonusRef.current = null;
        setSelectedBonus(bonus);
      }
    },
    [hasPaymentMethodChangedSinceDepositComplete, isBonusesDisabled, resetCompletedDepositState],
  );

  const activatedBonus = internalActivatedBonus;

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);

    if (!depositAmount || Number.isNaN(amount)) {
      return 0;
    }

    const bonus = internalActivatedBonus || selectedBonus || intendedBonusRef.current;

    if (!bonus) {
      return 0;
    }

    const minDeposit = getMinDeposit(bonus);

    return Math.max(minDeposit - amount, 0);
  }, [depositAmount, getMinDeposit, internalActivatedBonus, selectedBonus]);

  const showBonus =
    !hasActivatedSportsbookBonus &&
    (Boolean(internalActivatedBonus || depositCompleteBonus) ||
      !typesToHide.includes(CASINO_PRODUCT_TYPE));

  const bonusesList = useMemo(() => {
    if (internalActivatedBonus) {
      return [internalActivatedBonus];
    }

    if (depositCompleteBonus) {
      return [depositCompleteBonus];
    }

    return depositBonusesToShow;
  }, [depositBonusesToShow, depositCompleteBonus, internalActivatedBonus]);

  const exposedDepositBonuses = useMemo(() => {
    if (internalActivatedBonus) {
      return [internalActivatedBonus];
    }

    if (depositCompleteBonus) {
      return [depositCompleteBonus];
    }

    return depositBonuses;
  }, [depositBonuses, depositCompleteBonus, internalActivatedBonus]);

  const shouldRenderInSlider = forcedSliderMode || bonusesList.length > 1;

  return useMemo(
    () => ({
      activatedBonus,
      amountToGetBonus,
      availableBonuses: bonusesList,
      bonusesList,
      depositBonuses: exposedDepositBonuses,
      inputValue: depositAmount,
      intendedBonusRef,
      isBonusesDisabled,
      isLoading,
      paymentMethod: resolvedPaymentMethod,
      paymentMethodId: resolvedPaymentMethod?.id ?? null,
      selectedBonus,
      setSelectedBonus: handleSetSelectedBonus,
      shouldRenderInSlider,
      showBonus,
    }),
    [
      activatedBonus,
      amountToGetBonus,
      bonusesList,
      depositAmount,
      exposedDepositBonuses,
      handleSetSelectedBonus,
      isBonusesDisabled,
      isLoading,
      resolvedPaymentMethod,
      selectedBonus,
      shouldRenderInSlider,
      showBonus,
    ],
  );
};

export const QuickDepositBonusProvider = ({ children }) => {
  const [depositAmount, setDepositAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const bonusContextValue = useQuickDepositBonusValue({
    depositAmount,
    paymentMethod,
  });

  const handleSetPaymentMethodId = useCallback(paymentMethodId => {
    setPaymentMethod(currentPaymentMethod => {
      if (paymentMethodId == null) {
        return null;
      }

      if (currentPaymentMethod?.id === paymentMethodId) {
        return currentPaymentMethod;
      }

      return { ...(currentPaymentMethod || {}), id: paymentMethodId };
    });
  }, []);

  const value = useMemo(
    () => ({
      ...bonusContextValue,
      depositAmount,
      inputValue: depositAmount,
      paymentMethod,
      paymentMethodId: paymentMethod?.id ?? null,
      setDepositAmount,
      setInputValue: setDepositAmount,
      setPaymentMethod,
      setPaymentMethodId: handleSetPaymentMethodId,
    }),
    [bonusContextValue, depositAmount, handleSetPaymentMethodId, paymentMethod],
  );

  return (
    <QuickDepositBonusContext.Provider value={value}>{children}</QuickDepositBonusContext.Provider>
  );
};

QuickDepositBonusProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
